import { auth, firebaseConfig } from './firebase';

/** localStorage / sessionStorage key — set to `"1"` to log rich listener errors. */
export const FIRESTORE_LISTEN_DEBUG_STORAGE_KEY = 'lctf_firestore_listen_debug';

const HINT_ONCE_KEY = 'lctf_firestore_listen_debug_hint_once';

export type FirestoreListenAuthMeta = {
  userId?: string;
  role?: string;
  email?: string;
};

/**
 * Opt-in deep logging for Firestore `onSnapshot` failures.
 *
 * Enable any of:
 * - `localStorage.setItem("lctf_firestore_listen_debug", "1")` then reload
 * - `sessionStorage.setItem("lctf_firestore_listen_debug", "1")` then reload
 * - Open the app with query `?lctfFirestoreListenDebug=1` (persists to localStorage)
 */
export function isFirestoreListenDebugEnabled(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    if (window.localStorage?.getItem(FIRESTORE_LISTEN_DEBUG_STORAGE_KEY) === '1') return true;
    if (window.sessionStorage?.getItem(FIRESTORE_LISTEN_DEBUG_STORAGE_KEY) === '1') return true;
    return new URLSearchParams(window.location.search).get('lctfFirestoreListenDebug') === '1';
  } catch {
    return false;
  }
}

/** Call once on boot so `?lctfFirestoreListenDebug=1` sticks without hunting DevTools. */
export function persistFirestoreListenDebugFromUrl(): void {
  try {
    if (typeof window === 'undefined') return;
    const v = new URLSearchParams(window.location.search).get('lctfFirestoreListenDebug');
    if (v === '1') {
      window.localStorage.setItem(FIRESTORE_LISTEN_DEBUG_STORAGE_KEY, '1');
    }
  } catch {
    /* ignore quota / private mode */
  }
}

function permissionDenied(err: unknown): boolean {
  return typeof err === 'object' && err !== null && 'code' in err && (err as { code?: string }).code === 'permission-denied';
}

function errorCode(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'code' in err) return String((err as { code?: unknown }).code);
  return 'unknown';
}

function errorMessage(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'message' in err) return String((err as { message?: unknown }).message);
  return String(err);
}

/**
 * Shared handler for real-time listener failures. Use as the third argument to `onSnapshot`.
 *
 * When debug is off: logs a single `console.warn` for `permission-denied` (plus a one-time hint).
 * When debug is on: logs `console.error` with auth snapshot, project id, and rule-area hints.
 */
export function firestoreSnapshotListenError(
  context: string,
  listenId: string,
  err: unknown,
  authMeta?: FirestoreListenAuthMeta,
): void {
  const code = errorCode(err);
  const message = errorMessage(err);
  const label = `[Firestore listener] ${context} → ${listenId}`;
  const verbose = isFirestoreListenDebugEnabled();
  const u = auth.currentUser;

  if (verbose) {
    const ruleHint =
      code === 'permission-denied'
        ? 'permission-denied: compare Firestore rules for this collection vs users/{uid} (role, supplierOrgId), JWT email domain, and anonymous vs Google sign-in. ' +
          'People Hub paths (timeOffRequests, employeePayrollSnapshots, settings/employee_benefits) are excluded from the CRM wildcard and use tighter rules.'
        : '';
    console.error(label, {
      code,
      message,
      firebaseProjectId: firebaseConfig.projectId,
      authUid: u?.uid ?? null,
      authAnonymous: u?.isAnonymous ?? null,
      authEmail: u?.email ?? null,
      authProvider: u?.providerData?.[0]?.providerId ?? null,
      authMeta: authMeta ?? null,
      ruleHint,
      err,
    });
    return;
  }

  console.warn(label, code, message);
  if (permissionDenied(err)) {
    try {
      if (typeof window !== 'undefined' && window.sessionStorage?.getItem(HINT_ONCE_KEY) !== '1') {
        window.sessionStorage.setItem(HINT_ONCE_KEY, '1');
        console.info(
          `[Firestore] For full listener debug: localStorage.setItem("${FIRESTORE_LISTEN_DEBUG_STORAGE_KEY}","1"); location.reload()`,
        );
      }
    } catch {
      /* ignore */
    }
  }
}
